import React, { useEffect, useMemo, useRef, useState } from 'react';
import Popper from '@mui/material/Popper';
import DataTable from '../../../../common/_custom/DataTable/DataTable';
import { ComponentCellRenderer } from '../../../../common/_custom/DataTable/components/CellRenderers/ComponentCellRenderer/ComponentCellRenderer';
import { DataTableColumn, DataTableColumnType } from '../../../../common/_custom/DataTable/DataTable.types';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { useOutsideClick } from '../../../../utils/constants/hooks';
import styles from './GstOverview.module.scss';
import { GstOverviewRow, HsnSacEntry } from '../../../../store/dashboard/gstOverview/types';

// Close the popover on any scroll EXCEPT scrolls that originate inside the
// popover itself (otherwise scrolling the long HSN/SAC list inside the .MoreCard
// would dismiss what the user is trying to read). Capture phase so it catches
// scrolls in any nested scroll container, not just window.
function useCloseOnScroll(open: boolean, popoverRef: React.RefObject<HTMLElement>, close: () => void) {
    useEffect(() => {
        if (!open) return;
        const handler = (e: Event) => {
            const target = e.target as Node | null;
            if (target && popoverRef.current && popoverRef.current.contains(target)) return;
            close();
        };
        window.addEventListener('scroll', handler, true);
        return () => window.removeEventListener('scroll', handler, true);
    }, [open, close, popoverRef]);
}

const COLUMN_FIELDS = {
    GST: 'gst',
    TRADE_NAME: 'tradeName',
    NATURE_OF_BUSINESS: 'natureOfBusiness',
    HSN_SAC: 'hsnSac',
    ADDRESS: 'address',
} as const;

const MAX_INLINE_HSN_SAC = 3;
const MAX_INLINE_NOB = 3;

// GST status colour rule (RECUR-26035): BE returns the canonical display string
// (title-cased, "Suo-moto Cancelled", etc — see polus mergeGstOverview /
// formatGstStatus). Pill colour is bucketed into three categories
// (case-insensitive match here): Active=green, Inactive=grey, anything else
// (cancelled variants, suspended, provisional, not migrated, …) = red. Same
// logic as the India-map hover popover.
function statusClass(status: string): string {
    const s = (status || '').toLowerCase().trim();
    if (s === 'active') return styles.StatusActive;
    if (s === 'inactive') return styles.StatusInactive;
    return styles.StatusOther;
}

function GstinCell({ row }: { row: GstOverviewRow }) {
    return (
        <div className={styles.GstinCell}>
            <span className={styles.GstinStateName}>{row.state}</span>
            <Tooltip content={row.gstin} direction="top" usePortal fitContent>
                <span className={styles.GstinNumber}>{row.gstin}</span>
            </Tooltip>
            <span className={`${styles.StatusBadge} ${statusClass(row.status)}`}>{row.status}</span>
        </div>
    );
}

function TradeNameCell({ tradeName }: { tradeName: string | null }) {
    if (!tradeName) return <span style={{ color: '#7a7a9d' }}>—</span>;
    return (
        <div className={styles.TradeNameCell} title={tradeName}>
            {tradeName}
        </div>
    );
}

function NobCell({ items }: { items: string[] }) {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    useOutsideClick(anchorRef, () => setOpen(false));
    useCloseOnScroll(open, popoverRef, () => setOpen(false));

    if (items.length === 0) return <span style={{ color: '#7a7a9d', fontSize: '0.75rem' }}>—</span>;

    const visible = items.slice(0, MAX_INLINE_NOB);
    const extra = items.slice(MAX_INLINE_NOB);

    return (
        <div className={styles.HsnSacGroup} ref={anchorRef}>
            <ul className={styles.HsnSacList}>
                {visible.map((nob, i) => (
                    <li key={`${nob}-${i}`} className={styles.HsnSacItem} title={nob}>
                        <span className={styles.HsnSacDesc}>{nob}</span>
                    </li>
                ))}
            </ul>
            {extra.length > 0 && (
                <>
                    <span
                        className={styles.MoreLink}
                        role="button"
                        tabIndex={0}
                        onClick={() => setOpen((o) => !o)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setOpen((o) => !o);
                            }
                        }}
                    >
                        view all
                    </span>
                    <Popper
                        open={open}
                        anchorEl={anchorRef.current}
                        placement="bottom-end"
                        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                        style={{ zIndex: 1300 }}
                    >
                        <div ref={popoverRef} className={`${styles.MoreCard} ${styles.MoreCardNob}`}>
                            <ul className={styles.HsnSacMorePopover}>
                                {items.map((nob, i) => (
                                    <li key={`nob-all-${i}`} className={styles.HsnSacMoreItem}>
                                        <span className={styles.HsnSacMoreDesc}>{nob}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Popper>
                </>
            )}
        </div>
    );
}

function HsnSacCell({ hsn, sac }: { hsn: HsnSacEntry[]; sac: HsnSacEntry[] }) {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    useOutsideClick(anchorRef, () => setOpen(false));
    useCloseOnScroll(open, popoverRef, () => setOpen(false));

    const combined = [
        ...hsn.map((h) => ({ code: h.code, description: h.description, type: 'HSN' })),
        ...sac.map((s) => ({ code: s.code, description: s.description, type: 'SAC' })),
    ];

    if (combined.length === 0) return <span style={{ color: '#7a7a9d', fontSize: '0.75rem' }}>—</span>;

    const visible = combined.slice(0, MAX_INLINE_HSN_SAC);
    const extra = combined.slice(MAX_INLINE_HSN_SAC);

    return (
        <div className={styles.HsnSacGroup} ref={anchorRef}>
            <ul className={styles.HsnSacList}>
                {visible.map((item, i) => (
                    <li key={`${item.type}-${item.code}-${i}`} className={styles.HsnSacItem} title={item.description}>
                        <span className={styles.HsnSacDesc}>{item.description || ''}</span>
                        <span className={styles.HsnSacCode}>
                            ({item.type}:{item.code})
                        </span>
                    </li>
                ))}
            </ul>
            {extra.length > 0 && (
                <>
                    <span
                        className={styles.MoreLink}
                        role="button"
                        tabIndex={0}
                        onClick={() => setOpen((o) => !o)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setOpen((o) => !o);
                            }
                        }}
                    >
                        view all
                    </span>
                    <Popper
                        open={open}
                        anchorEl={anchorRef.current}
                        placement="bottom-end"
                        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
                        style={{ zIndex: 1300 }}
                    >
                        <div ref={popoverRef} className={styles.MoreCard}>
                            <ul className={styles.HsnSacMorePopover}>
                                {combined.map((item, i) => (
                                    <li key={`all-${item.type}-${item.code}-${i}`} className={styles.HsnSacMoreItem}>
                                        <span className={styles.HsnSacMoreDesc}>{item.description || ''} </span>
                                        <span className={styles.HsnSacMoreCode}>
                                            ({item.type}:{item.code})
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Popper>
                </>
            )}
        </div>
    );
}

function AddressCell({ address }: { address: string }) {
    if (!address) return <span style={{ color: '#7a7a9d' }}>—</span>;
    return (
        <div className={styles.AddressCell} title={address}>
            {address}
        </div>
    );
}

interface GstinTableProps {
    rows: GstOverviewRow[];
}

function GstinTable({ rows }: Readonly<GstinTableProps>) {
    // Percentage widths sum to 100% so the table fits the container (no
    // horizontal scroll). With table-layout: fixed (set by shared DataTable),
    // percentages distribute the actual table width across columns. Proportions
    // mirror the Figma 1143px reference (189/164/200/295/295) → 17/14/18/26/26.
    const columns: DataTableColumn[] = useMemo(
        () => [
            {
                field: COLUMN_FIELDS.GST,
                headerLabel: 'GST',
                headerOriginalLabel: 'GST',
                type: DataTableColumnType.COMPONENT,
                width: '17%',
            },
            {
                field: COLUMN_FIELDS.TRADE_NAME,
                headerLabel: 'Trade Name',
                headerOriginalLabel: 'Trade Name',
                type: DataTableColumnType.COMPONENT,
                width: '14%',
            },
            {
                field: COLUMN_FIELDS.NATURE_OF_BUSINESS,
                headerLabel: 'Nature of Business',
                headerOriginalLabel: 'Nature of Business',
                type: DataTableColumnType.COMPONENT,
                width: '18%',
            },
            {
                field: COLUMN_FIELDS.HSN_SAC,
                headerLabel: 'HSN & SAC',
                headerOriginalLabel: 'HSN & SAC',
                type: DataTableColumnType.COMPONENT,
                width: '25%',
            },
            {
                field: COLUMN_FIELDS.ADDRESS,
                headerLabel: 'Address',
                headerOriginalLabel: 'Address',
                type: DataTableColumnType.COMPONENT,
                width: '26%',
            },
        ],
        [],
    );

    const componentRenderFnRegistry = ({ row, column }: { row: GstOverviewRow; column?: DataTableColumn }) => {
        switch (column?.field) {
            case COLUMN_FIELDS.GST:
                return <GstinCell row={row} />;
            case COLUMN_FIELDS.TRADE_NAME:
                return <TradeNameCell tradeName={row.tradeName} />;
            case COLUMN_FIELDS.NATURE_OF_BUSINESS:
                return <NobCell items={row.natureOfBusiness || []} />;
            case COLUMN_FIELDS.HSN_SAC:
                return <HsnSacCell hsn={row.hsn || []} sac={row.sac || []} />;
            case COLUMN_FIELDS.ADDRESS:
                return <AddressCell address={row.address} />;
            default:
                return null;
        }
    };

    const customCellRenderers = useMemo(
        () => ({
            [DataTableColumnType.COMPONENT]: (props: any) => (
                <ComponentCellRenderer {...props} dynamicProps={{ componentRenderFnRegistry }} />
            ),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows],
    );

    return (
        <div className={styles.GstinTableSection}>
            <div className={styles.GstinTableHeader}>GST Details</div>
            <div className={styles.TableWrap}>
                <div className={styles.TableInnerFrame}>
                    <DataTable
                        columns={columns}
                        rows={rows}
                        totalRowCount={rows.length}
                        emptyState={<div className={styles.EmptyState}>No GST data available</div>}
                        customCellRenderers={customCellRenderers}
                    />
                </div>
            </div>
        </div>
    );
}

export default GstinTable;
